/** SaaS Landing Page starter — navbar, hero, features, pricing, testimonials, footer. */

export const SAAS_LANDING_FILES = {
  'src/App.js': `import React from 'react';
import './styles.css';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Features from './components/Features';
import Pricing from './components/Pricing';
import Testimonials from './components/Testimonials';
import CtaSection from './components/CtaSection';
import Footer from './components/Footer';

export default function App() {
  return (
    <div className="saas-root">
      <Navbar />
      <Hero />
      <Features />
      <Pricing />
      <Testimonials />
      <CtaSection />
      <Footer />
    </div>
  );
}
`,

  'src/styles.css': `* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; }
.saas-root { min-height: 100vh; background: #fafaf8; color: #111827; }
.section { padding: 64px 24px; max-width: 1120px; margin: 0 auto; }
.section-title { font-size: clamp(26px, 3vw, 36px); font-weight: 800; letter-spacing: -0.03em; text-align: center; margin-bottom: 12px; }
.section-sub { font-size: 15px; color: #6b7280; text-align: center; max-width: 480px; margin: 0 auto 40px; line-height: 1.6; }
.accent { color: #10b981; }
.btn-primary {
  display: inline-flex; align-items: center; padding: 12px 24px; border-radius: 8px;
  background: #10b981; color: #fff; font-size: 14px; font-weight: 700; text-decoration: none;
  border: none; cursor: pointer; box-shadow: 0 2px 8px rgba(16,185,129,0.35);
}
.btn-secondary {
  display: inline-flex; align-items: center; padding: 12px 20px; border-radius: 8px;
  background: #fff; color: #374151; font-size: 14px; font-weight: 600; text-decoration: none;
  border: 1px solid #e5e7eb; cursor: pointer;
}
`,

  'src/components/Navbar.js': `import React, { useState, useEffect } from 'react';

const LINKS = [
  { label: 'Product', href: '#features' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Customers', href: '#testimonials' },
  { label: 'Docs', href: '#docs' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 50, background: scrolled ? 'rgba(250,250,248,0.95)' : '#fafaf8',
      borderBottom: scrolled ? '1px solid #e5e7eb' : '1px solid transparent',
      backdropFilter: scrolled ? 'blur(12px)' : 'none',
      boxShadow: scrolled ? '0 1px 8px rgba(0,0,0,0.05)' : 'none',
      transition: 'all 0.2s ease',
    }}>
      <div style={{
        maxWidth: 1120, margin: '0 auto', display: 'flex', alignItems: 'center',
        padding: '0 24px', height: 56,
      }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', marginRight: 'auto' }}>
          <div style={{
            width: 28, height: 28, borderRadius: 7, background: '#111827',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981', fontSize: 14,
          }}>✓</div>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Launchpad</span>
        </a>
        <nav style={{ display: 'flex', gap: 4 }}>
          {LINKS.map(({ label, href }) => (
            <a key={label} href={href} style={{ padding: '5px 12px', fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>{label}</a>
          ))}
        </nav>
        <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
          <a href="#login" style={{ padding: '6px 14px', fontSize: 13, color: '#374151', textDecoration: 'none' }}>Sign in</a>
          <a href="#signup" className="btn-primary" style={{ padding: '7px 16px', fontSize: 13 }}>Start free trial</a>
        </div>
      </div>
    </header>
  );
}
`,

  'src/components/Hero.js': `import React from 'react';

export default function Hero() {
  return (
    <section style={{
      background: '#fafaf8', padding: '72px 24px 64px', borderBottom: '1px solid #e5e7eb',
    }}>
      <div style={{
        maxWidth: 1120, margin: '0 auto', display: 'flex', alignItems: 'center',
        gap: 64, flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 380px', maxWidth: 480 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 20,
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 20, padding: '4px 12px',
            fontSize: 12, fontWeight: 600, color: '#059669',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
            Trusted by 2,400+ engineering teams
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 800, lineHeight: 1.1, marginBottom: 20 }}>
            Ship production software <span className="accent">10× faster</span>
          </h1>
          <p style={{ fontSize: 16, color: '#6b7280', lineHeight: 1.7, marginBottom: 24, maxWidth: 420 }}>
            Launchpad turns product requirements into deployable, tested code. AI agents handle architecture, QA, and CI/CD.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
            <a href="#signup" className="btn-primary">Start free trial →</a>
            <a href="#demo" className="btn-secondary">See how it works</a>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Used by teams at</span>
            {['Stripe', 'Vercel', 'Linear', 'Notion'].map((name) => (
              <span key={name} style={{
                fontSize: 11, fontWeight: 700, color: '#6b7280', padding: '3px 10px',
                border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff',
              }}>{name}</span>
            ))}
          </div>
        </div>
        <div style={{
          flex: '1 1 400px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
          padding: 20, boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        }}>
          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 12, fontWeight: 600 }}>DEPLOYMENT DASHBOARD</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
            {[['Uptime', '99.9%'], ['Deploys', '847'], ['Teams', '2.4k']].map(([l, v]) => (
              <div key={l} style={{ background: '#f9fafb', borderRadius: 8, padding: 12, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 10, color: '#9ca3af' }}>{l}</div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 80 }}>
            {[42, 68, 55, 80, 62, 90, 74].map((h, i) => (
              <div key={i} style={{
                flex: 1, height: h + '%', background: 'linear-gradient(180deg, #10b981, #059669)',
                borderRadius: '4px 4px 0 0', minHeight: 8,
              }} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Features.js': `import React from 'react';

const FEATURES = [
  { icon: '⚡', title: 'AI Code Generation', desc: 'Turn specs into production-ready React, API routes, and tests in minutes.' },
  { icon: '🔒', title: 'Sandboxed Execution', desc: 'Every build runs in an isolated environment with security scanning built in.' },
  { icon: '🔗', title: 'Git Integration', desc: 'Auto-commit, branch, and open PRs — your workflow stays in sync.' },
  { icon: '📊', title: 'Live Analytics', desc: 'Track generation quality, token usage, and deploy success from one dashboard.' },
  { icon: '🧪', title: 'Automated QA', desc: 'Test agents validate structure, types, and runtime before you merge.' },
  { icon: '🚀', title: 'One-Click Deploy', desc: 'Ship to staging or production with CI/CD pipelines generated for you.' },
];

export default function Features() {
  return (
    <section id="features" className="section">
      <h2 className="section-title">Everything you need to <span className="accent">ship faster</span></h2>
      <p className="section-sub">From idea to production — one platform for AI-assisted full-stack development.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
        {FEATURES.map((f) => (
          <article key={f.title} style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}>
            <span style={{ fontSize: 28, display: 'block', marginBottom: 12 }}>{f.icon}</span>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{f.title}</h3>
            <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>{f.desc}</p>
          </article>
        ))}
      </div>
    </section>
  );
}
`,

  'src/components/Pricing.js': `import React from 'react';

const PLANS = [
  { name: 'Starter', price: '$0', period: '/mo', desc: 'For side projects', features: ['3 projects', 'Basic AI agents', 'Community support'], cta: 'Get started', highlight: false },
  { name: 'Pro', price: '$49', period: '/mo', desc: 'For growing teams', features: ['Unlimited projects', 'All agents + RAG', 'Priority support', 'GitHub sync'], cta: 'Start free trial', highlight: true },
  { name: 'Enterprise', price: 'Custom', period: '', desc: 'For large orgs', features: ['SSO & audit logs', 'Dedicated sandbox', 'SLA & onboarding'], cta: 'Contact sales', highlight: false },
];

export default function Pricing() {
  return (
    <section id="pricing" className="section" style={{ background: '#f9fafb', maxWidth: '100%' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <h2 className="section-title">Simple, transparent <span className="accent">pricing</span></h2>
        <p className="section-sub">Start free. Scale when your team is ready.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, alignItems: 'stretch' }}>
          {PLANS.map((plan) => (
            <article key={plan.name} style={{
              background: '#fff', border: plan.highlight ? '2px solid #10b981' : '1px solid #e5e7eb',
              borderRadius: 14, padding: 28, display: 'flex', flexDirection: 'column', gap: 16,
              boxShadow: plan.highlight ? '0 8px 32px rgba(16,185,129,0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
            }}>
              {plan.highlight && (
                <span style={{
                  fontSize: 10, fontWeight: 700, color: '#059669', background: '#f0fdf4',
                  border: '1px solid #bbf7d0', padding: '3px 10px', borderRadius: 20, width: 'fit-content',
                }}>Most popular</span>
              )}
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 700 }}>{plan.name}</h3>
                <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{plan.desc}</p>
              </div>
              <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em' }}>
                {plan.price}<span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>{plan.period}</span>
              </div>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                {plan.features.map((f) => (
                  <li key={f} style={{ fontSize: 13, color: '#374151', display: 'flex', gap: 8 }}>
                    <span style={{ color: '#10b981' }}>✓</span> {f}
                  </li>
                ))}
              </ul>
              <button className={plan.highlight ? 'btn-primary' : 'btn-secondary'} style={{ width: '100%', justifyContent: 'center' }}>
                {plan.cta}
              </button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Testimonials.js': `import React from 'react';

const QUOTES = [
  { name: 'Sarah Kim', role: 'VP Engineering, Stripe', text: 'Launchpad cut our prototype cycle from weeks to hours. The generated code is surprisingly production-ready.' },
  { name: 'Marcus Chen', role: 'CTO, Linear', text: 'We ship features 10× faster. The AI agents understand our stack and keep our conventions.' },
  { name: 'Elena Rodriguez', role: 'Lead Dev, Vercel', text: 'Best investment for our platform team. Sandboxed runs and Git sync just work.' },
];

export default function Testimonials() {
  return (
    <section id="testimonials" className="section">
      <h2 className="section-title">Loved by <span className="accent">engineering teams</span></h2>
      <p className="section-sub">See what builders say about shipping with Launchpad.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
        {QUOTES.map((q) => (
          <blockquote key={q.name} style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24,
            margin: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}>
            <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 16, fontStyle: 'italic' }}>
              "{q.text}"
            </p>
            <footer>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{q.name}</div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>{q.role}</div>
            </footer>
          </blockquote>
        ))}
      </div>
    </section>
  );
}
`,

  'src/components/CtaSection.js': `import React from 'react';

export default function CtaSection() {
  return (
    <section id="cta" style={{
      background: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)',
      padding: '72px 24px',
      textAlign: 'center',
    }}>
      <div style={{ maxWidth: 640, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center' }}>
        <span style={{
          fontSize: 11, fontWeight: 700, color: '#6ee7b7', textTransform: 'uppercase',
          letterSpacing: '0.12em', background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)',
          padding: '4px 14px', borderRadius: 20,
        }}>Ready to ship?</span>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: '#fff', lineHeight: 1.15, margin: 0 }}>
          Start building <span style={{ color: '#10b981' }}>10× faster</span> today
        </h2>
        <p style={{ fontSize: 16, color: '#9ca3af', lineHeight: 1.65, maxWidth: 480 }}>
          Join 2,400+ teams using Launchpad to turn ideas into production-ready software. Free trial — no credit card.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <a href="#signup" className="btn-primary">Start free trial →</a>
          <a href="#demo" className="btn-secondary" style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.2)', color: '#e5e7eb' }}>
            Book a demo
          </a>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Footer.js': `import React from 'react';

const COLUMNS = {
  Product: ['Features', 'Pricing', 'Changelog', 'Docs'],
  Company: ['About', 'Blog', 'Careers', 'Press'],
  Resources: ['Community', 'Help Center', 'Status', 'API'],
  Legal: ['Privacy', 'Terms', 'Security'],
};

export default function Footer() {
  return (
    <footer style={{ background: '#fafaf8', borderTop: '1px solid #e5e7eb' }}>
      <div style={{
        maxWidth: 1120, margin: '0 auto', display: 'flex', gap: 48, flexWrap: 'wrap',
        padding: '52px 24px 44px',
      }}>
        <div style={{ flex: '0 0 220px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 26, height: 26, borderRadius: 6, background: '#111827',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981', fontSize: 12,
            }}>✓</div>
            <span style={{ fontSize: 14, fontWeight: 700 }}>Launchpad</span>
          </div>
          <p style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.65 }}>Ship production software 10× faster with AI agents.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 32px', flex: 1, minWidth: 0 }}>
          {Object.entries(COLUMNS).map(([heading, links]) => (
            <div key={heading}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>{heading}</div>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {links.map((l) => (
                  <li key={l}><a href={'#' + l.toLowerCase()} style={{ fontSize: 13, color: '#9ca3af', textDecoration: 'none' }}>{l}</a></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <div style={{
        maxWidth: 1120, margin: '0 auto', borderTop: '1px solid #e5e7eb',
        padding: '16px 24px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        fontSize: 12, color: '#d1d5db',
      }}>
        <span>© 2025 Launchpad Inc.</span>
        <span>Made with ⚡ for builders</span>
      </div>
    </footer>
  );
}
`,
};
